/* bs-footer — accordion animado para las columnas 2/3/4 en mobile.
   Mismo patron que bs-faq-help.js: intercepta toggle nativo de <details>/<summary>
   y anima height del body via rAF. Mantiene la semantica accesible.

   Detalle clave: en desktop (>=768px segun Rafa), todas las columnas deben quedar
   abiertas y el chevron oculto. El JS detecta via matchMedia y desactiva la
   intercepcion del click en desktop, dejando que el browser gestione el state
   nativamente (Rafa puede forzar open via CSS, pero si el browser permite cerrarlas
   queremos no-op del JS para no animar).

   Reduced-motion: respetamos prefers-reduced-motion. En ese caso el toggle es
   instantaneo (sin animacion height) — semantica intacta para keyboard/screen
   readers. */

(function () {
  'use strict';

  const MOBILE_QUERY = '(max-width: 767px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  function setupAccordion(card) {
    const summary = card.querySelector('.bs-footer__col-summary');
    const body = card.querySelector('.bs-footer__col-body');
    if (!summary || !body) return;

    let animating = false;

    function isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    function prefersReducedMotion() {
      return window.matchMedia(REDUCED_MOTION_QUERY).matches;
    }

    // En tablet+ las cols deben arrancar abiertas. El UA de <details> oculta
    // los hijos cuando no hay [open], asi que necesitamos setearlo al cargar.
    // Listener resize→matchMedia ajusta on-the-fly si el user redimensiona.
    function syncOpenState() {
      // Defensivo: si el card ya no esta en el DOM (re-render via section:load),
      // limpiar el listener para evitar memory leak.
      if (!card.isConnected) {
        if (typeof mq.removeEventListener === 'function') {
          mq.removeEventListener('change', syncOpenState);
        } else if (typeof mq.removeListener === 'function') {
          mq.removeListener(syncOpenState);
        }
        return;
      }
      if (!isMobile() && !card.hasAttribute('open')) {
        card.setAttribute('open', '');
      }
    }
    const mq = window.matchMedia(MOBILE_QUERY);
    syncOpenState();
    // Listener para cambio de viewport (resize/orientationchange). matchMedia
    // listeners no aceptan {passive} — son sync events, no scroll-related.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', syncOpenState);
    } else if (typeof mq.addListener === 'function') {
      // Fallback Safari <14
      mq.addListener(syncOpenState);
    }

    function clearTransitionEnd(handler) {
      body.removeEventListener('transitionend', handler);
    }

    function animateOpen() {
      animating = true;
      // [open] activa el contenido; el browser lo monta a auto height.
      card.setAttribute('open', '');

      // Reduced motion: skip animacion, dejar al browser su layout nativo.
      if (prefersReducedMotion()) {
        animating = false;
        return;
      }

      const target = body.scrollHeight;
      // Bajamos a 0 inline (override el auto que aplico el browser).
      body.style.height = '0px';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          body.style.height = target + 'px';
        });
      });

      function onEnd(e) {
        if (e.propertyName !== 'height') return;
        clearTransitionEnd(onEnd);
        // Quitamos inline height para que crezca naturalmente si content cambia.
        body.style.height = '';
        animating = false;
      }
      body.addEventListener('transitionend', onEnd);
    }

    function animateClose() {
      animating = true;

      // Reduced motion: cerrar instantaneo sin animacion height.
      if (prefersReducedMotion()) {
        card.removeAttribute('open');
        body.style.height = '';
        animating = false;
        return;
      }

      const start = body.scrollHeight;
      body.style.height = start + 'px';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          body.style.height = '0px';
        });
      });

      function onEnd(e) {
        if (e.propertyName !== 'height') return;
        clearTransitionEnd(onEnd);
        // Removemos [open] al final de la animacion (no antes — sino el browser oculta el contenido).
        card.removeAttribute('open');
        body.style.height = '';
        animating = false;
      }
      body.addEventListener('transitionend', onEnd);
    }

    // Click listener — NO passive porque usamos preventDefault para interceptar
    // el toggle nativo de <details>. Click events no tienen scrolling overhead
    // asociado (passive solo aplica a touchstart/touchmove/wheel).
    summary.addEventListener('click', function (event) {
      // En desktop (>=768) NO animamos pero SI bloqueamos el toggle nativo:
      // las cols deben quedarse always-open via CSS de Rafa. Si dejamos al
      // browser togglear [open] con keyboard (Enter en summary tab-focusable),
      // el contenido se oculta a pesar de nuestra CSS — el UA aplica
      // display:none a los hijos de <details> sin [open]. Bloqueando el toggle
      // garantizamos visibilidad estable en tablet+ para keyboard users.
      if (!isMobile()) {
        event.preventDefault();
        // Asegurar [open] activo (defensivo: si el merchant agrega manualmente
        // el atributo al markup o el browser lo quita por cualquier motivo).
        if (!card.hasAttribute('open')) {
          card.setAttribute('open', '');
        }
        return;
      }

      event.preventDefault();
      if (animating) return;
      if (card.hasAttribute('open')) {
        animateClose();
      } else {
        animateOpen();
      }
    });
  }

  function init(root) {
    const cards = (root || document).querySelectorAll('[data-bs-footer-accordion]');
    cards.forEach(function (card) {
      // Idempotent: si ya esta inicializado (mismo nodo), salir.
      if (card.__bsFooterInit) return;
      card.__bsFooterInit = true;
      setupAccordion(card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Soporte Shopify section reload en theme editor — re-inicializa solo dentro
  // del nodo recargado (cards nuevas, flag __bsFooterInit no presente).
  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target.querySelector('.bs-footer');
    if (section) init(event.target);
  });
})();
